import React, { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import AppModal from '../../shared/AppModal';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import Swal from 'sweetalert2';
import axios from '../../utils/axios';
import { baseUrl, onboardingInviteCreate, onboardingInviteList, onboardingInviteRevoke, onboardingInviteDelete, getLanguageCampaign, getDesignations, getCompanies } from '../../utils/urls';
import './onboardingAdmin.css';

function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
    });
}

function isExpired(expiresAt) {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
}

function statusInfo(invite) {
    if (invite.is_used || invite.status === 'used') return { label: 'Used', cls: 'oba-badge oba-badge-used' };
    if (invite.is_revoked || invite.status === 'revoked') return { label: 'Revoked', cls: 'oba-badge oba-badge-revoked' };
    if (isExpired(invite.expires_at) || invite.status === 'expired') return { label: 'Expired', cls: 'oba-badge oba-badge-expired' };
    return { label: 'Active', cls: 'oba-badge oba-badge-active' };
}

const EMPTY_FORM = {
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    expires_in_days: 7,
    ghl_location_ids: [{
        "location_id": "zhQ2Qiuw48levTle3u0M",
        "location_name": "LIVE Long Island HFS"
    }],
    allowed_teams: [],
    designation_id: null,
    company_id: null,
    active_pipeline: 'zhQ2Qiuw48levTle3u0M',
    application_pipeline: 'self',
};

export default function OnboardingAdmin() {
    const toast = useRef(null);

    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [creating, setCreating] = useState(false);

    // Newly created invite result (to show the URL)
    const [newInvite, setNewInvite] = useState(null);
    // console.log("form", form)
    // Reference data for ghl_defaults
    const [activeLocations, setActiveLocations] = useState([]);
    const [activeTeams, setActiveTeams] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [companies, setCompanies] = useState([]);

    const fetchInvites = () => {
        setLoading(true);
        axios.get(onboardingInviteList)
            .then(res => setInvites(Array.isArray(res.data) ? res.data : (res.data.results || [])))
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load invites', life: 3000 }))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchInvites(); }, []);

    useEffect(() => {
        axios.get(getLanguageCampaign)
            .then(res => {
                setActiveLocations(res.data.active_locations || []);
                setActiveTeams(res.data.active_teams || []);
            })
            .catch(() => { });
        axios.get(getDesignations)
            .then(res => setDesignations(res.data || []))
            .catch(() => { });
        axios.get(getCompanies)
            .then(res => setCompanies(res.data || []))
            .catch(() => { });
    }, []);

    const openDialog = () => {
        setForm(EMPTY_FORM);
        setFormErrors({});
        setNewInvite(null);
        setDialogOpen(true);
    };

    const validate = () => {
        const errs = {};
        if (!form.first_name.trim()) errs.first_name = 'Required';
        if (!form.last_name.trim()) errs.last_name = 'Required';
        if (!form.phone.trim()) errs.phone = 'Required';
        if (!form.expires_in_days || form.expires_in_days < 1) errs.expires_in_days = 'Must be at least 1';
        if (!form.ghl_location_ids.length) errs.ghl_location_ids = 'Required';
        return errs;
    };

    const handleCreate = () => {
        const errs = validate();
        if (Object.keys(errs).length) { setFormErrors(errs); return; }
        setCreating(true);
        axios.post(onboardingInviteCreate, {
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim(),
            phone: form.phone.trim(),
            email: form.email.trim() || undefined,
            expires_in_days: form.expires_in_days,
            ghl_defaults: {
                ghl_location_ids: form.ghl_location_ids.map(l => l.location_id || l),
                allowedTeams: form.allowed_teams.map(t => t.team_id || t),
                designation_id: form.designation_id || '',
                company_id: form.company_id || '',
                active_pipeline: form.active_pipeline || '',
                application_pipeline: form.application_pipeline || 'self',
            },
        })
            .then(res => {
                setNewInvite(res.data);
                fetchInvites();
                toast.current?.show({ severity: 'success', summary: 'Invite Created', detail: `Link sent for ${form.first_name}`, life: 3000 });
            })
            .catch(err => {
                const msg = err?.response?.data?.detail || err?.response?.data?.error || 'Failed to create invite';
                toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
            })
            .finally(() => setCreating(false));
    };

    const handleRevoke = (invite) => {
        Swal.fire({
            title: 'Revoke Invite?',
            text: `This will invalidate the link for ${invite.first_name} ${invite.last_name}.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, revoke it',
            confirmButtonColor: '#ef4444',
            background: '#13131a',
            color: '#e2e8f0',
        }).then(result => {
            if (!result.isConfirmed) return;
            axios.post(onboardingInviteRevoke(invite.id))
                .then(() => {
                    fetchInvites();
                    toast.current?.show({ severity: 'info', summary: 'Revoked', detail: 'Invite has been revoked', life: 3000 });
                })
                .catch(err => {
                    const msg = err?.response?.data?.detail || 'Failed to revoke';
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                });
        });
    };

    const handleDelete = (invite) => {
        Swal.fire({
            title: 'Delete Invite?',
            text: `This will permanently delete the invite for ${invite.first_name} ${invite.last_name}.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it',
            confirmButtonColor: '#ef4444',
            background: '#13131a',
            color: '#e2e8f0',
        }).then(result => {
            if (!result.isConfirmed) return;
            axios.delete(onboardingInviteDelete(invite.id))
                .then(() => {
                    fetchInvites();
                    toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Invite has been deleted', life: 3000 });
                })
                .catch(err => {
                    const msg = err?.response?.data?.detail || 'Failed to delete invite';
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                });
        });
    };

    const copyUrl = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            toast.current?.show({ severity: 'success', summary: 'Copied!', detail: 'Invite URL copied to clipboard', life: 2000 });
        });
    };

    const setField = (key, val) => {
        setForm(prev => ({ ...prev, [key]: val }));
        if (formErrors[key]) setFormErrors(prev => ({ ...prev, [key]: undefined }));
    };

    // Filtered rows
    const filtered = invites.filter(inv => {
        const q = search.toLowerCase();
        return (
            inv.first_name?.toLowerCase().includes(q) ||
            inv.last_name?.toLowerCase().includes(q) ||
            inv.email?.toLowerCase().includes(q) ||
            inv.phone?.includes(q)
        );
    });

    // ── Column templates ───────────────────────────────────────────────
    const nameCol = (row) => (
        <div className="oba-name-cell">
            <div className="oba-avatar">{row.first_name?.[0]}{row.last_name?.[0]}</div>
            <div>
                <div className="oba-name">{row.first_name} {row.last_name}</div>
                <div className="oba-sub">{row.email || '—'}</div>
            </div>
        </div>
    );

    const phoneCol = (row) => <span className="oba-mono">{row.phone || '—'}</span>;

    const statusCol = (row) => {
        const { label, cls } = statusInfo(row);
        return <span className={cls}>{label}</span>;
    };

    const expiresCol = (row) => {
        const used = row.is_used || row.status === 'used';
        const revoked = row.is_revoked || row.status === 'revoked';
        return (
            <div>
                <div style={{ fontSize: 13 }}>{formatDate(row.expires_at)}</div>
                {isExpired(row.expires_at) && !used && !revoked && (
                    <div style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>Expired</div>
                )}
            </div>
        );
    };

    const createdCol = (row) => <span style={{ fontSize: 13 }}>{formatDate(row.created_at)}</span>;

    const actionsCol = (row) => {
        const used = row.is_used || row.status === 'used';
        const revoked = row.is_revoked || row.status === 'revoked';
        const canRevoke = !used && !revoked && !isExpired(row.expires_at);
        return (
            <div className="oba-actions">
                {row.onboarding_url && (
                    <button
                        className="oba-btn oba-btn-copy"
                        title="Copy invite URL"
                        onClick={() => copyUrl(row.onboarding_url)}
                    >
                        <i className="pi pi-copy" /> Copy URL
                    </button>
                )}
                {canRevoke && (
                    <button
                        className="oba-btn oba-btn-revoke"
                        title="Revoke invite"
                        onClick={() => handleRevoke(row)}
                    >
                        <i className="pi pi-ban" /> Revoke
                    </button>
                )}
                {!used && (
                    <button
                        className="oba-btn oba-btn-revoke"
                        title="Delete invite"
                        onClick={() => handleDelete(row)}
                    >
                        <i className="pi pi-trash" /> Delete
                    </button>
                )}
            </div>
        );
    };

    // ── Dialog footer ──────────────────────────────────────────────────
    const dialogFooter = newInvite ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="oba-btn oba-btn-secondary" onClick={() => setDialogOpen(false)}>Close</button>
        </div>
    ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="oba-btn oba-btn-secondary" onClick={() => setDialogOpen(false)} disabled={creating}>Cancel</button>
            <button className="oba-btn oba-btn-primary" onClick={handleCreate} disabled={creating}>
                {creating ? <><i className="pi pi-spin pi-spinner" /> Creating…</> : <><i className="pi pi-send" /> Send Invite</>}
            </button>
        </div>
    );

    return (
        <div className="oba-root">
            <Toast ref={toast} />

            {/* Header */}
            <div className="oba-header">
                <div>
                    <h1 className="oba-title">Onboarding Invites</h1>
                    <p className="oba-subtitle">Create and manage invite links for new team members</p>
                </div>
                <button className="oba-btn oba-btn-primary oba-btn-lg" onClick={openDialog}>
                    <i className="pi pi-user-plus" /> New Invite
                </button>
            </div>

            {/* Stats row */}
            <div className="oba-stats">
                {[
                    { label: 'Total', value: invites.length, icon: 'pi-users', color: '#6c63ff' },
                    { label: 'Active', value: invites.filter(i => !(i.is_used || i.status === 'used') && !(i.is_revoked || i.status === 'revoked') && !isExpired(i.expires_at)).length, icon: 'pi-check-circle', color: '#22c55e' },
                    { label: 'Used', value: invites.filter(i => (i.is_used || i.status === 'used')).length, icon: 'pi-user', color: '#a78bfa' },
                    { label: 'Expired', value: invites.filter(i => !(i.is_used || i.status === 'used') && !(i.is_revoked || i.status === 'revoked') && isExpired(i.expires_at)).length, icon: 'pi-clock', color: '#f59e0b' },
                ].map(s => (
                    <div key={s.label} className="oba-stat-card">
                        <i className={`pi ${s.icon}`} style={{ color: s.color, fontSize: 22 }} />
                        <div>
                            <div className="oba-stat-val">{s.value}</div>
                            <div className="oba-stat-label">{s.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Table */}
            <div className="oba-table-wrap">
                <div className="oba-table-toolbar">
                    <div className="oba-search-wrap">
                        <i className="pi pi-search oba-search-icon" />
                        <InputText
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search by name, email or phone…"
                            className="oba-search"
                        />
                    </div>
                    <button className="oba-btn oba-btn-ghost" onClick={fetchInvites}>
                        <i className="pi pi-refresh" /> Refresh
                    </button>
                </div>

                <DataTable
                    value={filtered}
                    loading={loading}
                    emptyMessage="No invites found"
                    stripedRows
                    className="oba-datatable"
                    paginator
                    rows={15}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                    currentPageReportTemplate="{first}–{last} of {totalRecords}"
                >
                    <Column header="Name / Email" body={nameCol} style={{ minWidth: 220 }} />
                    <Column header="Phone" body={phoneCol} style={{ minWidth: 140 }} />
                    <Column header="Status" body={statusCol} style={{ minWidth: 100 }} />
                    <Column header="Expires" body={expiresCol} style={{ minWidth: 180 }} />
                    <Column header="Created" body={createdCol} style={{ minWidth: 180 }} />
                    <Column header="Actions" body={actionsCol} style={{ minWidth: 200 }} />
                </DataTable>
            </div>

            {/* Create Invite Dialog */}
            <AppModal
                header="Send Onboarding Invite"
                visible={dialogOpen}
                onHide={() => !creating && setDialogOpen(false)}
                footer={dialogFooter}
                panelStyle={{ width: 580 }}
            >
                {newInvite ? (
                    /* Success view */
                    <div className="oba-success">
                        <div className="oba-success-icon">🎉</div>
                        <h3>Invite Created!</h3>
                        <p>Share this link with <strong>{newInvite.first_name} {newInvite.last_name}</strong>:</p>
                        <div className="oba-url-box">
                            <span className="oba-url-text">{newInvite.onboarding_url}</span>
                            <button
                                className="oba-btn oba-btn-copy oba-btn-sm"
                                onClick={() => copyUrl(newInvite.onboarding_url)}
                            >
                                <i className="pi pi-copy" />
                            </button>
                        </div>
                        <div className="oba-expire-note">
                            <i className="pi pi-clock" /> Expires {formatDate(newInvite.expires_at)}
                        </div>
                    </div>
                ) : (
                    /* Form view */
                    <div className="oba-form">
                        <div className="oba-form-row">
                            <div className="oba-form-field">
                                <label className="oba-form-label">First Name <span className="oba-req">*</span></label>
                                <InputText
                                    value={form.first_name}
                                    onChange={e => setField('first_name', e.target.value)}
                                    placeholder="Jane"
                                    className={`oba-input${formErrors.first_name ? ' oba-input-err' : ''}`}
                                />
                                {formErrors.first_name && <span className="oba-err-msg">{formErrors.first_name}</span>}
                            </div>
                            <div className="oba-form-field">
                                <label className="oba-form-label">Last Name <span className="oba-req">*</span></label>
                                <InputText
                                    value={form.last_name}
                                    onChange={e => setField('last_name', e.target.value)}
                                    placeholder="Smith"
                                    className={`oba-input${formErrors.last_name ? ' oba-input-err' : ''}`}
                                />
                                {formErrors.last_name && <span className="oba-err-msg">{formErrors.last_name}</span>}
                            </div>
                        </div>

                        <div className="oba-form-field">
                            <label className="oba-form-label">Phone <span className="oba-req">*</span></label>
                            <InputText
                                value={form.phone}
                                onChange={e => setField('phone', e.target.value)}
                                placeholder="+1 (555) 000-0000"
                                className={`oba-input${formErrors.phone ? ' oba-input-err' : ''}`}
                            />
                            {formErrors.phone && <span className="oba-err-msg">{formErrors.phone}</span>}
                        </div>

                        <div className="oba-form-field">
                            <label className="oba-form-label">Email
                                {/* <span className="oba-optional">(optional)</span> */}
                            </label>
                            <InputText
                                value={form.email}
                                onChange={e => setField('email', e.target.value)}
                                placeholder="jane@example.com"
                                className="oba-input"
                            />
                        </div>

                        <div className="oba-form-field">
                            <label className="oba-form-label">Link Expires In (days) <span className="oba-req">*</span></label>
                            <InputNumber
                                value={form.expires_in_days}
                                onValueChange={e => setField('expires_in_days', e.value)}
                                min={1} max={90} showButtons
                                className={`oba-input-num${formErrors.expires_in_days ? ' oba-input-err' : ''}`}
                            />
                            {formErrors.expires_in_days && <span className="oba-err-msg">{formErrors.expires_in_days}</span>}
                        </div>

                        <div style={{ borderTop: '1px solid var(--oba-line)', paddingTop: 14, marginTop: 2 }}>
                            {/* <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--oba-faint)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
                                GHL Defaults <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                            </div> */}

                            <div className="oba-form" style={{ gap: 12 }}>
                                <div className="oba-form-row">
                                    <div className="oba-form-field">
                                        <label className="oba-form-label">Locations <span className="oba-req">*</span></label>
                                        <MultiSelect
                                            value={form.ghl_location_ids}
                                            onChange={e => setField('ghl_location_ids', e.value)}
                                            options={activeLocations}
                                            optionLabel="location_name"
                                            filter
                                            placeholder="Select Locations"
                                            maxSelectedLabels={2}
                                            className={`oba-input${formErrors.ghl_location_ids ? ' oba-input-err' : ''}`}
                                            panelClassName="oba-ms-panel"
                                            style={{ padding: 0, border: 'none' }}
                                        />
                                        {formErrors.ghl_location_ids && <span className="oba-err-msg">{formErrors.ghl_location_ids}</span>}
                                    </div>
                                    <div className="oba-form-field">
                                        <label className="oba-form-label">Teams</label>
                                        <MultiSelect
                                            value={form.allowed_teams}
                                            onChange={e => setField('allowed_teams', e.value)}
                                            options={activeTeams}
                                            optionLabel="name"
                                            filter
                                            placeholder="Select Teams"
                                            maxSelectedLabels={2}
                                            className="oba-input"
                                            panelClassName="oba-ms-panel"
                                            style={{ padding: 0, border: 'none' }}
                                        />
                                    </div>
                                </div>

                                <div className="oba-form-row">
                                    <div className="oba-form-field">
                                        <label className="oba-form-label">Designation</label>
                                        <Dropdown
                                            value={form.designation_id}
                                            onChange={e => setField('designation_id', e.value)}
                                            options={designations.map(d => ({ label: d.title, value: d.id }))}
                                            placeholder="Select Designation"
                                            className="oba-input"
                                            panelClassName="oba-dd-panel"
                                            style={{ padding: 0, border: 'none' }}
                                        />
                                    </div>
                                    <div className="oba-form-field">
                                        <label className="oba-form-label">Company</label>
                                        <Dropdown
                                            value={form.company_id}
                                            onChange={e => setField('company_id', e.value)}
                                            options={companies.map(c => ({ label: c.title, value: c.id }))}
                                            placeholder="Select Company"
                                            className="oba-input"
                                            panelClassName="oba-dd-panel"
                                            style={{ padding: 0, border: 'none' }}
                                        />
                                    </div>
                                </div>

                                <div className="oba-form-row">
                                    <div className="oba-form-field">
                                        <label className="oba-form-label">Active Pipeline</label>
                                        <Dropdown
                                            value={form.active_pipeline}
                                            onChange={e => setField('active_pipeline', e.value)}
                                            options={activeLocations.map(l => ({ label: l.location_name, value: l.location_id }))}
                                            placeholder="Select Pipeline"
                                            className="oba-input"
                                            panelClassName="oba-dd-panel"
                                            style={{ padding: 0, border: 'none' }}
                                        />
                                    </div>
                                    <div className="oba-form-field">
                                        <label className="oba-form-label">App Pipeline</label>
                                        <Dropdown
                                            value={form.application_pipeline}
                                            onChange={e => setField('application_pipeline', e.value)}
                                            options={[{ label: 'Self', value: 'self' }]}
                                            placeholder="Select"
                                            className="oba-input"
                                            panelClassName="oba-dd-panel"
                                            style={{ padding: 0, border: 'none' }}
                                        />
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                )}
            </AppModal>
        </div>
    );
}
